import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    FormControlLabel,
    FormGroup,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Transition, formatPhoneNumber } from './utils';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export default function StatusDialog({
    order,
    setOrder,
    payment,
    setPayment,
    isOpen,
    openDialog,
    closeDialog
}) {
    const {t, i18n} = useTranslation();
    const [details, setDetails] = useState({
        phone: sessionStorage.getItem('phone') ?? '',
        email: sessionStorage.getItem('email') ?? '',
        switchNotify: false,
        switchReceipt: false,
        switchNotes: false,
        notes: ''
    });
    const paymentSubmitted = useRef(false);

    const handleSwitchChange = useCallback(({target: {name, checked}}) => {
        setDetails((prev) => ({
          ...prev,
          [name]: checked,
        }));
    }, []);

    const handleInputChange = useCallback(({target: {name, value}}) => {
        const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;
        setDetails((prev) => ({ 
            ...prev,
            [name]: formattedValue
        }));
    }, []);

    const handleOnClose = useCallback(async () => {
        if (payment.paymentMethod.success) {
            try {
                await fetch(`${process.env.REACT_APP_API_URL}/order/customer`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${payment.orderToken}`
                    },
                    body: JSON.stringify({
                        customer: {
                            phone: details.phone?.replace(/\D/g, '') || '',
                            email: details.email || '',
                            smsNotifs: details.switchNotify,
                            emailNotifs: details.switchReceipt,
                        },
                        notes: details.notes
                    })
                });
                sessionStorage.setItem('phone', details.phone ?? '');
                sessionStorage.setItem('email', details.email ?? '');
            }
            catch (error) {
                console.error(error);
            }
            setOrder([]);
            setPayment(null);
        }
        closeDialog('paymentStatus');
    }, [closeDialog,
        details,
        payment,
        setOrder,
        setPayment
    ]);

    const processPayment = useCallback(async () => {
        if (!paymentSubmitted.current) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/order`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${payment.orderToken}`,
                    },
                    body: JSON.stringify({
                        items: order,
                        payment: {
                            type: payment.paymentMethod.type,
                            success: payment.paymentMethod.success,
                            transactionId: payment.paymentMethod.transactionId,
                            metadata: payment.paymentMethod.metadata,
                            error: payment.paymentMethod.error,
                            subtotal: payment.subtotal,
                            tip: payment.tip,
                            cityTax: payment.cityTax,
                            stateTax: payment.stateTax,
                            total: payment.total
                        },
                    }),
                });
                if (res.ok) {
                    paymentSubmitted.current = true;
                }
            }
            catch (error) {
                console.error(error);
            }
        }
    }, [order, payment]);

    useEffect(() => {
        processPayment();
    }, [processPayment]);

    return (
        <Dialog
            open={isOpen('paymentStatus')}
            onClose={() => closeDialog('paymentStatus')}
            keepMounted
            fullScreen
            slots={{transition: Transition}}
            slotProps={{
                paper: {
                    sx: {
                        position: 'relative',
                        maxHeight: '100vh',
                    }
                },
            }}> 
        <DialogTitle>
            <Box display="flex" alignItems="center">
                <IconButton onClick={handleOnClose} edge="start" sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">{t('paymentStatus')}</Typography>
            </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1 }}>
            {payment?.paymentMethod?.success && (<>
                <Box display="flex" justifyContent="center"  mt={2}>
                    <CheckCircleIcon style={{ color: 'green', fontSize: 60 }} />
                </Box>
                <Box display="flex" justifyContent="center">
                    <Typography variant="subtitle1">
                        {t('paymentOk')}
                    </Typography>
                </Box>
                <FormGroup sx={{
                            flexDirection: 'column',
                            gap: 1,
                            padding: 2,
                            marginTop: 2
                    }}>
                    <FormControlLabel
                        control={
                        <Switch
                            checked={details.switchNotify}
                            onChange={handleSwitchChange}
                            name="switchNotify"
                        />
                        }
                        label={t('allowNotifications')}
                        labelPlacement="start"
                        sx={{
                            justifyContent: 'space-between',
                            margin: 0,
                    }}/>
                    {details.switchNotify && (
                        <>
                            <TextField
                                fullWidth
                                placeholder={t('paymentMethodPhoneNumberLabel')}
                                name="phone"
                                value={details.phone || ''}
                                onChange={handleInputChange}
                                type="tel"
                                sx={{
                                    mt: -1,
                                    '& .MuiInputLabel-root': {
                                    color: '#BDBDBD',
                                    },
                                }}
                            />
                            <Typography variant='caption' sx={{margin: 0, p: 0}}>
                                <Trans
                                    i18nKey="smsDisclaimer"
                                    components={[
                                        <NavLink target="_blank" rel="noopener noreferrer" to={'https://www.prinnovainc.com/sms-terms?lang=' + i18n.language} style={{ textDecoration: 'none', color: '#4B7359'}}/>,
                                        <NavLink target="_blank" rel="noopener noreferrer" to={'https://www.prinnovainc.com/privacy?lang=' + i18n.language} style={{ textDecoration: 'none', color: '#4B7359'}}/>
                                    ]}    
                                />
                            </Typography>                            
                        </>
                    )}
                    <FormControlLabel
                        control={
                        <Switch
                            checked={details.switchReceipt}
                            onChange={handleSwitchChange}
                            name="switchReceipt"
                        />
                        }
                        label={t('printReceipt')}
                        labelPlacement="start"
                        sx={{
                            justifyContent: 'space-between',
                            margin: 0,
                    }}/>
                    {details.switchReceipt && (<>
                        <TextField
                            fullWidth
                            placeholder={t('paymentMethodEmailLabel')}
                            name="email"
                            value={details.email || ''}
                            onChange={handleInputChange}
                            type="email"
                            sx={{
                                mt: -1,
                                '& .MuiInputLabel-root': {
                                  color: '#BDBDBD',
                                },
                              }}
                        />
                        <Typography variant='caption' sx={{margin: 0, p: 0}}>
                            <Trans
                                i18nKey="emailDisclaimer"
                                components={[
                                    <NavLink to={'https://www.prinnovainc.com/email-terms?lang=' + i18n.language} style={{ textDecoration: 'none', color: '#4B7359'}}/>,
                                    <NavLink to={'https://www.prinnovainc.com/privacy?lang=' + i18n.language} style={{ textDecoration: 'none', color: '#4B7359'}}/>
                                ]}    
                            />
                        </Typography>
                        </>
                    )}
                   <FormControlLabel
                        control={
                        <Switch
                            checked={details.switchNotes}
                            onChange={handleSwitchChange}
                            name="switchNotes"
                        />
                        }
                        label={t('addNotes')}
                        labelPlacement="start"
                        sx={{
                            justifyContent: 'space-between',
                            margin: 0,
                    }}/>
                    {details.switchNotes && (
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder={t('paymentMethodNotesLabel')}
                            name="notes"
                            value={details.notes || ''}
                            onChange={handleInputChange}
                            type="text"
                            sx={{
                                mt: -1,
                                '& .MuiInputLabel-root': {
                                  color: '#BDBDBD',
                                },
                              }}/>
                    )}                    
                </FormGroup>
             </>)}

            {!payment?.paymentMethod?.success && (<>
                <Box display="flex" justifyContent="center" p={2}>
                    <CancelIcon style={{ color: 'red', fontSize: 60 }} />
                </Box>
                <Box display="flex" justifyContent="center" p={2}>
                    <Typography variant="subtitle1">
                        {t('paymentFail')}
                    </Typography>   
                </Box>
            </>)}

        </DialogContent>
        <DialogActions>
            <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleOnClose}>
                {t('finishButton')}
            </Button>
        </DialogActions>
    </Dialog>
    );
}